/**
 * BRAND RATING MODAL
 * 
 * Allows users to rate a brand across multiple categories
 * Must own at least one item from the brand to rate
 */

import { useState, useEffect } from 'react';
import { AppIcons } from '../../constants/icons';
import { getCurrentUser } from '../../firebase/auth';
import { rateBrand } from '../../services/brandService';
import type { Brand, BrandRating } from '../../types/brand';

interface BrandRatingModalProps {
  brand: Brand;
  existingRating?: BrandRating | null;
  onClose: () => void;
  onSaved: (rating: BrandRating) => void;
}

// Rating categories with descriptions
const RATING_CATEGORIES = [
  { 
    key: 'formulaQuality', 
    label: 'Formula Quality', 
    description: 'How well does it apply? Self-leveling, consistency, coverage?',
    icon: '✨'
  },
  { 
    key: 'colorAccuracy', 
    label: 'Color Accuracy', 
    description: 'Does the actual polish match the promotional photos?',
    icon: '🎨'
  },
  { 
    key: 'longevity', 
    label: 'Longevity', 
    description: 'How long does it wear without chipping or fading?',
    icon: '⏰'
  },
  { 
    key: 'customerService', 
    label: 'Customer Service', 
    description: 'Responsive to questions? Handles issues well?',
    icon: '💬'
  },
  { 
    key: 'shippingSpeed', 
    label: 'Shipping Speed', 
    description: 'How quickly do orders arrive after purchasing?',
    icon: '📦'
  },
  { 
    key: 'packaging', 
    label: 'Packaging', 
    description: 'Is it well-packaged? Good presentation?',
    icon: '🎁'
  },
  { 
    key: 'value', 
    label: 'Value', 
    description: 'Is the quality worth the price?',
    icon: '💰'
  },
  { 
    key: 'brushQuality', 
    label: 'Brush Quality', 
    description: 'Good brush shape and bristle quality?',
    icon: '🖌️'
  },
] as const;

type RatingKey = typeof RATING_CATEGORIES[number]['key'];

export default function BrandRatingModal({
  brand,
  existingRating,
  onClose,
  onSaved
}: BrandRatingModalProps) {
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [hoveredRating, setHoveredRating] = useState<{ key: string; value: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const currentUser = getCurrentUser();

  // Load existing ratings — whitelist by category key so any stray/legacy field
  // on the doc (e.g., an old aggregate `rating`) can't pollute state, blow up the
  // average (NaN), or get re-written and rejected by Firestore (undefined value).
  useEffect(() => {
    if (existingRating) {
      const scores: Record<string, number> = {};
      const raw = existingRating as unknown as Record<string, unknown>;
      for (const { key } of RATING_CATEGORIES) {
        const v = raw[key];
        if (typeof v === 'number') scores[key] = v;
      }
      setRatings(scores);
    }
  }, [existingRating]);

  // Handle rating change
  const handleRating = (key: RatingKey, value: number) => {
    setRatings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear a rating
  const clearRating = (key: RatingKey) => {
    setRatings(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // Submit ratings
  const handleSubmit = async () => {
    if (!currentUser) {
      setError('You must be logged in to rate a brand');
      return;
    }

    // Must have at least one rating
    if (Object.keys(ratings).length === 0) {
      setError('Please rate at least one category');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await rateBrand(brand.id, currentUser.uid, ratings);
      setSuccess(true);
      
      // Wait a moment then close
      setTimeout(() => {
        onSaved({
          id: currentUser.uid,
          userId: currentUser.uid,
          ...ratings,
          createdAt: new Date(),
          updatedAt: new Date()
        } as BrandRating);
      }, 1000);
    } catch (err: any) {
      console.error('Error saving rating:', err);
      setError(err.message || 'Failed to save rating');
    } finally {
      setSaving(false);
    }
  };

  // Star rating component
  const StarRating = ({ categoryKey, value }: { categoryKey: RatingKey; value?: number }) => {
    const currentHover = hoveredRating?.key === categoryKey ? hoveredRating.value : null;

    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => {
          const isActive = currentHover !== null
            ? star <= currentHover
            : value !== undefined && star <= value;

          return (
            <button
              key={star}
              type="button"
              onClick={() => handleRating(categoryKey, star)}
              onMouseEnter={() => setHoveredRating({ key: categoryKey, value: star })}
              onMouseLeave={() => setHoveredRating(null)}
              className={`p-1 transition-all transform hover:scale-110 ${
                isActive ? 'text-yellow-500' : 'text-slate-500'
              }`}
            >
              <AppIcons.Wishlist
                className={`w-6 h-6 ${isActive ? 'fill-yellow-500' : ''}`}
              />
            </button>
          );
        })}
        {value !== undefined && (
          <button
            type="button"
            onClick={() => clearRating(categoryKey)}
            className="ml-2 text-xs text-glass-muted hover:text-glass-secondary"
          >
            Clear
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="glass-card max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-600">
          <div>
            <h2 className="text-xl font-bold text-glass">
              Rate {brand.name}
            </h2>
            <p className="text-sm text-glass-muted">
              Share your experience to help other collectors
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-glass-muted hover:text-glass hover:bg-slate-600/50 rounded-full transition-colors"
          >
            <AppIcons.Close className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Success State */}
          {success && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-green-600/30 rounded-full flex items-center justify-center mb-4">
                <AppIcons.Confirm className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-glass mb-2">Thank You!</h3>
              <p className="text-glass-secondary">Your rating has been saved.</p>
            </div>
          )}

          {/* Rating Form */}
          {!success && (
            <>
              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-600/30 border border-red-500/50 rounded-lg text-red-300">
                  <AppIcons.Error className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Info */}
              <div className="p-3 mb-4 bg-blue-600/30 border border-blue-500/50 rounded-lg">
                <p className="text-sm text-blue-300">
                  💡 Only rate categories you have experience with. Skip ones you're unsure about.
                </p>
              </div>

              {/* Rating Categories */}
              <div className="space-y-4">
                {RATING_CATEGORIES.map(({ key, label, description, icon }) => (
                  <div
                    key={key}
                    className="p-4 border border-slate-600 rounded-lg hover:border-slate-500 transition-colors bg-slate-700/30"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{icon}</span>
                          <h3 className="font-medium text-glass">{label}</h3>
                        </div>
                        <p className="text-sm text-glass-muted">{description}</p>
                      </div>
                      <StarRating
                        categoryKey={key}
                        value={ratings[key]}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-slate-600/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-glass-secondary">
                    Categories rated: <strong className="text-glass">{Object.keys(ratings).length}</strong> of {RATING_CATEGORIES.length}
                  </span>
                  {Object.keys(ratings).length > 0 && (
                    <span className="text-sm text-glass-secondary">
                      Average: <strong className="text-glass">
                        {(Object.values(ratings).reduce((a, b) => a + b, 0) / Object.keys(ratings).length).toFixed(1)}
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-600 bg-slate-700/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-glass-secondary hover:bg-slate-600/50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || Object.keys(ratings).length === 0}
              className={`px-6 py-2 bg-purple-600 text-white rounded-lg font-medium transition-colors ${
                saving || Object.keys(ratings).length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-purple-700'
              }`}
            >
              {saving ? 'Saving...' : existingRating ? 'Update Rating' : 'Submit Rating'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
